import React, { useState, useEffect } from "react";
import accessService from "../../apis/Profile/AccessServiceApi";
import "./index.css";
import profileChecked from "../../assets/new_profile/profile-checked.svg";
import profileUnchecked from "../../assets/new_profile/profile-unchecked.svg";
import { getIconModule, getColorModule } from "./getIconColorModule";

// Define modules in a hierarchical structure

// NOTE: Other bundles temporarily commented out for initial launch
// TODO: Uncomment additional bundles when ready for full release
const PAID_PLANS_AVAILABLE = process.env.REACT_APP_BRANCH !== "main";

const bundleHierarchy = {
  freemium: {
    name: "Freemium",
    modules: [{ id: 1, name: "Company" }],
  },
  board: {
    name: "Business Dash Tier",
    modules: [
      { id: 1, name: "Company" },
      { id: 14, name: "Business" },
    ],
    includes: ["freemium"],
  },
  initiator: {
    name: "Build impact - Catalyst",
    modules: [
      // { id: 14, name: "Business" },
      // { id: 8, name: "Admin" },
      // { id: 9, name: "AR" },
      { id: 2, name: "Product" },

      //     { id: 6, name: "Feed" },
      //     { id: 15, name: "ERP" },
      //   ],
      //   includes: ["freemium"], // This bundle includes all freemium modules
      // },
      // ambition: {
      //   name: "Build Impact - Ambition",
      //   modules: [
      //     // { id: 7, name: "Education" },
      //     { id: 3, name: "Ambition" },
      //     // { id: 10, name: "Client" },

      //     // { id: 4, name: "Report" },
      //     // { id: 11, name: "Share" },
      //     // { id: 5, name: "Map" },

      // { id: 12, name: "Simulate" },
      // { id: 13, name: "Get Help" },
    ],
    includes: ["initiator", "freemium"], // This bundle includes all initiator and freemium modules
  },
  together: {
    name: "Better Together",
    modules: [{ id: 13, name: "Get Help" }],
    includes: ["freemium"],
  },
  education: {
    name: "Bitesize Education",
    modules: [{ id: 16, name: "Education" }],
    includes: ["freemium"],
  },
};

const DEFAULT_BUNDLE = "freemium";

// Helper function to get all module IDs from a bundle including inherited modules
export const getAllModuleIdsForBundle = (bundleKey) => {
  const bundle = bundleHierarchy[bundleKey];
  let moduleIds = [...bundle.modules.map((module) => module.id)];

  // Add modules from included bundles
  if (bundle.includes) {
    bundle.includes.forEach((includedBundleKey) => {
      const includedBundle = bundleHierarchy[includedBundleKey];
      moduleIds = [
        ...moduleIds,
        ...includedBundle.modules.map((module) => module.id),
      ];
    });
  }

  // Remove duplicates
  return [...new Set(moduleIds)];
};

// Helper function to determine which bundle a set of module IDs represents
const determineBundleFromModules = (selectedModuleIds) => {
  // Check from highest to lowest in hierarchy
  if (isSubset(getAllModuleIdsForBundle("ambition"), selectedModuleIds)) {
    return "ambition";
  } else if (
    isSubset(getAllModuleIdsForBundle("initiator"), selectedModuleIds)
  ) {
    return "initiator";
  } else if (isSubset(getAllModuleIdsForBundle("board"), selectedModuleIds)) {
    return "board";
  } else if (
    isSubset(getAllModuleIdsForBundle("together"), selectedModuleIds)
  ) {
    return "together";
  } else {
    return "freemium"; // Default/minimum
  }
};

// Helper function to check if all elements in arr1 are in arr2
const isSubset = (arr1, arr2) => {
  return arr1.every((item) => arr2.includes(item));
};

export const SimplifiedNavigationTree = ({
  onSelect,
  isAccessManager,
  isWriteOnly,
}) => {
  const [permission, setPermission] = useState(
    isAccessManager ? "write" : "read",
  );
  const [selectedBundle, setSelectedBundle] = useState(DEFAULT_BUNDLE);
  const [selectedModuleIds, setSelectedModuleIds] = useState(
    getAllModuleIdsForBundle(DEFAULT_BUNDLE),
  );

  // When bundle changes, update selected modules
  useEffect(() => {
    const moduleIds = getAllModuleIdsForBundle(selectedBundle);
    setSelectedModuleIds(moduleIds);

    onSelect && onSelect(moduleIds, permission, [selectedBundle]);
  }, [selectedBundle, permission]);

  const handleBundleChange = (bundleKey) => {
    if (!PAID_PLANS_AVAILABLE && bundleKey !== "freemium") return;
    setSelectedBundle(bundleKey);
  };

  return (
    <div className="navigation-tree navigation-new-modules">
      <div
        style={{
          color: "#193666",
          marginBottom: "10px",
          fontSize: "16px",
          textAlign: "center",
        }}
      >
        Select a bundle
      </div>

      <div className="permission-tabs">
        {!isWriteOnly && (
          <button
            className={`tab ${permission === "read" ? "active" : ""}`}
            onClick={() => setPermission("read")}
          >
            Read Only
          </button>
        )}
        <button
          className={`tab ${permission === "write" ? "active" : ""}`}
          onClick={() => setPermission("write")}
        >
          Read + Write
        </button>
      </div>

      <div className="bundle-selection" style={{ marginBottom: "20px" }}>
        {Object.keys(bundleHierarchy).map((bundleKey) => {
          const isSoon = !PAID_PLANS_AVAILABLE && bundleKey !== "freemium";
          return (
          <div key={bundleKey} style={{ margin: "10px 0" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                cursor: isSoon ? "default" : "pointer",
                opacity: isSoon ? 0.7 : 1,
              }}
            >
              <input
                type="radio"
                name="bundle"
                checked={selectedBundle === bundleKey}
                onChange={() => !isSoon && handleBundleChange(bundleKey)}
                style={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  width: "20px",
                  height: "20px",
                  background: `url(${
                    selectedBundle === bundleKey
                      ? profileChecked
                      : profileUnchecked
                  }) center/contain no-repeat`,
                  marginRight: "8px",
                }}
                disabled={isSoon}
              />
              <span style={{ fontWeight: "500", textTransform: "capitalize" }}>
                {bundleHierarchy[bundleKey].name} Bundle
              </span>
              {isSoon && (
                <span
                  style={{
                    marginLeft: "8px",
                    background: "rgba(11,29,58,0.06)",
                    color: "rgba(11,29,58,0.7)",
                    padding: "0.15rem 0.5rem",
                    borderRadius: 999,
                    fontSize: "0.65rem",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  ⏱ Coming soon
                </span>
              )}
            </label>
          </div>
          );
        })}
      </div>

      <div className="modules-included" style={{ marginTop: "20px" }}>
        <h3
          style={{
            color: "#193666",
            marginBottom: "10px",
            fontSize: "16px",
            textAlign: "center",
          }}
        >
          Modules Included:
        </h3>

        {Object.entries(bundleHierarchy).map(([bundleKey, bundle]) => {
          // Only show bundles that are included in the selected bundle
          if (
            bundleKey === selectedBundle ||
            (bundleHierarchy[selectedBundle].includes &&
              bundleHierarchy[selectedBundle].includes.includes(bundleKey))
          ) {
            return (
              <div key={bundleKey} className="category-section">
                <div className="category-header">{bundle.name}</div>
                <div className="module-list">
                  {bundle.modules.map((module) => (
                    <div
                      key={module.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        margin: "5px 0",
                      }}
                    >
                      <label
                        className={`module-item ${
                          bundle.name === "Build impact - Catalyst"
                            ? "profile-dash"
                            : "profile-module"
                        }`}
                        style={{
                          backgroundColor: getColorModule(module.name),
                          minWidth: "136px",
                          maxWidth: "136px",
                        }}
                      >
                        <img
                          src={getIconModule(module.name)}
                          alt="Build Impact - Ambition Icon"
                          width={20}
                          height={20}
                        />
                        <span className="module-name">{module.name}</span>
                      </label>
                      <div
                        className="included-indicator"
                        style={{
                          marginLeft: "8px",
                          fontSize: "12px",
                          color: "#4CAF50",
                        }}
                      >
                        ✓ Included
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

const AccessRequestModal = ({ isOpen, onClose, isInvite = false }) => {
  const [email, setEmail] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [permission, setPermission] = useState("read");
  const [selectedBundles, setSelectedBundles] = useState([]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isInvite) {
        await accessService.inviteUser({
          email,
          permission,
          modules: selectedItems,
          bundles: selectedBundles,
        });
      } else {
        await accessService.requestAccess({
          email,
          permission,
          modules: selectedItems,
          bundles: selectedBundles,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error requesting access:", error);
    }
  };

  // Get module names from IDs for display
  const getModuleNamesFromIds = (moduleIds) => {
    const allModules = Object.values(bundleHierarchy).flatMap(
      (category) => category.modules,
    );

    return moduleIds.map((id) => {
      const module = allModules.find((m) => m.id === id);
      return module ? module.name : `ID: ${id}`;
    });
  };

  return (
    <div
      className="modal-overlay-profile"
      style={{ backdropFilter: "blur(5px)" }}
    >
      <div className="modal-content-navigation">
        <div className="modal-header">
          <h2>Invite Accesses to your IntraNet</h2>
          <button onClick={onClose} className="close-button">
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="modal-grid">
            <div className="left-panel">
              <SimplifiedNavigationTree
                onSelect={(items, perm, bundles) => {
                  setSelectedItems(items);
                  setPermission(perm);
                  setSelectedBundles(bundles);
                }}
              />
            </div>
            <div className="right-panel">
              <div className="email-form">
                <p className="form-description" style={{ fontWeight: "bold" }}>
                  Below is a list of the requested accesses:
                </p>
                <div className="selected-modules">
                  {selectedBundles.length > 0 && (
                    <div>
                      <p style={{ marginBottom: "8px" }}>
                        <strong>Selected Bundle:</strong>
                      </p>
                      <ul>
                        {selectedBundles.map((bundle) => (
                          <li
                            key={bundle}
                            style={{ textTransform: "capitalize" }}
                          >
                            {bundleHierarchy[bundle].name} Bundle
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedItems.length > 0 && (
                    <div>
                      <p style={{ marginBottom: "8px" }}>
                        <strong>Included Modules:</strong>
                      </p>
                      <ul>
                        {getModuleNamesFromIds(selectedItems).map(
                          (name, index) => (
                            <li key={index}>{name}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Send mail to:</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@email.com"
                      required
                    />
                  </div>
                  <div className="form-footer">
                    <button type="submit" className="submit-button">
                      Send
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessRequestModal;
